import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ViewPlanBuilderPageLayout from "../../../../layout/pitch-deck-analysis-page/view";
import { useSupabaseApp } from "../../../../SupabaseApp";
import useBusinessPlanBuilder from "../../../hooks/useBusinessPlanBuilder";
import LoadingModal from "../../../modals/loading-modal";
import { closeAlert } from "./helpers";
import "./pitch-deck-analysis.css";

/**
 * ViewPitchDeckAnalysis Component
 *
 * Read-only view of completed pitch deck analysis documents. Displays pitch deck
 * information in a viewable format without edit capabilities. Fetches pitch deck data
 * from Supabase using pitchDeckId from URL parameters on component mount. Uses custom
 * useBusinessPlanBuilder hook for state management. Displays loading modal while
 * fetching data, then renders content via ViewPlanBuilderPageLayout. Implements
 * fetch protection with hasFetched state to prevent duplicate API calls.
 * Requires user authentication.
 *
 * @returns {JSX.Element} Read-only view of pitch deck analysis
 */
const ViewPitchDeckAnalysis = () => {
  // State to control alert component visibility
  const [alert, setAlert] = useState(true);

  // State to store alert message (initially shows LoadingModal)
  const [message, setMessage] = useState(<LoadingModal />);

  // State to prevent unnecessary duplicate API calls
  const [hasFetched, setHasFetched] = useState(false);

  // Get current user from Supabase for authentication
  const { supabase } = useSupabaseApp();

  // Supabase function to fetch pitch deck document from database
  // const { getMyPitchDeckDev } = supabase.functions;

  // Get pitchDeckId from URL route parameter
  const { pitchDeckId } = useParams();

  // Get pitch deck state and dispatcher from custom hook (renamed to 'data' for clarity)
  const { state: data, dispatch } = useBusinessPlanBuilder();

  // Props object for ViewPlanBuilderPageLayout component
  const props = {
    data: data,
    setMessage,
    setAlert,
    message,
    alert,
  };

  // Params object with closeAlert handler function
  const params = {
    ...props,
    closeAlert: () => closeAlert(props),
  };

  /**
   * Fetch pitch deck data from Supabase and initialize display state
   * Uses pitchDeckId from URL params to retrieve the document
   * Parses JSON response and populates form state for rendering
   * Includes error handling and hasFetched guard to prevent duplicate calls
   */
  useEffect(() => {
    // Fetch pitch deck data
    async function getPitchDeck(id) {
      try {
        const { data, error } = await supabase.functions.invoke(
          "getMyPitchDeckDev",
          {
            body: {
              pitchDeckId: id,
            },
          },
        );
        const pitchDeck = data;
        dispatch({ type: "initialize", value: pitchDeck });

        setAlert(false);
        setHasFetched(true);
      } catch (err) {
        console.error("Couldn't fetch data from Realm");
        setMessage("Failed to fetch data. Please try again later.");
        setAlert(true);
      }
    }

    if (pitchDeckId && !hasFetched) {
      getPitchDeck(pitchDeckId);
    }
  }, [pitchDeckId, hasFetched, dispatch, supabase]);

  // useEffect(() => {
  //   console.log("data:", data);
  // }, [data]);

  return <ViewPlanBuilderPageLayout props={params} />;
};

export default ViewPitchDeckAnalysis;
